// Snow recipe — plan §4.2: "as sand w/ different params + mask(slope) coverage + shadow
// blue-shift"; needs the `lightModulatedRamp` blend mode. Immutable built-in root.
//
// DEVIATION from the plan (2026-08-09): the mask(slope) coverage layer is GONE — see the note in
// `params` below. Slope-driven bare-rock belongs in the splat field, not in this recipe.
//
// This recipe is the ONE that is deliberately a near-clone of another (sand) at different params,
// per the plan — but it is written as a ROOT rather than an `extend` of sand, because the layer
// STACK differs (it appends the mask + blue-shift layers and drops the sparkle's ramp semantics),
// and D10 restricts `extend` to append/disable. A user variant of THIS recipe is of course free.
//
// HONEST EXPECTATION (plan §4.3): snow is "good, but lighting-bound" — a convincing snow needs
// translucency + strong ambient, which this lighting stage does not have. The blue-shift is the
// cheap fake the plan explicitly calls for, and without it snow reads as white plastic.
//
// SCALE CONTRACT:
//   drifts  ~0.8 m wavelength, warped ~0.6 m, ~1.5 cm relief — wind-sculpted surface undulation
//   crust   ~5 cm fbm, ~1 mm — the granular crust texture
//   glints  ~6 mm scatter — ice-crystal sparkle (AA-gated exactly like sand's, §7)
import {MaterialLayer, MaterialRecipe, ParamSchema} from "../@MaterialTypes.js";
import {colorRamp} from "../RecipeUtils.js";

export const SNOW_RECIPE_ID = "snow";

export const snowRecipe = new MaterialRecipe({
	id: SNOW_RECIPE_ID,
	name: "Snow",
	roughness: 0.55,
	params: [// The continuous snow surface (2026-08-08): subtle mounds over large areas, textured at every
		// zoom. Height-only — the colour is the drift ramp's (and its octaves survive the band-limit
		// at every zoom where the drifts show, so there is no "solid colour" zone anywhere).
		new ParamSchema({key: "terrain_scale", label: "Terrain cell (m)", kind: "scalar", min: 0.2, max: 20, default: 2.5}),
		new ParamSchema({key: "terrain_octaves", label: "Terrain octaves", kind: "scalar", min: 1, max: 7, default: 5}),
		new ParamSchema({key: "terrain_height", label: "Terrain relief (m)", kind: "scalar", min: 0, max: 0.05, default: 0.004}),
		new ParamSchema({key: "drift_wavelength", label: "Drift spacing (m)", kind: "scalar", min: 0.05, max: 10, default: 0.8}),
		new ParamSchema({key: "drift_angle", label: "Drift angle (deg)", kind: "scalar", min: 0, max: 360, default: 20}),
		new ParamSchema({key: "drift_sharpness", label: "Drift sharpness", kind: "scalar", min: 0.05, max: 8, default: 1.4}),
		new ParamSchema({key: "drift_warpScale", label: "Drift warp scale (m)", kind: "scalar", min: 0.05, max: 20, default: 2.2}),
		new ParamSchema({key: "drift_warpAmount", label: "Drift warp amount (m)", kind: "scalar", min: 0, max: 5, default: 0.6}),
		// Drift field: broad meandering swathes, soft. veining ~1.1 = one/two swell systems across
		// the field; sharpness 0.6 < 1 = a broad swell, never a closed loop line (Set3 §3.3/§3.5).
		new ParamSchema({key: "drift_scale", label: "Drift cell (m)", kind: "scalar", min: 0.2, max: 20, default: 1.4}),
		new ParamSchema({key: "drift_veining", label: "Drift density", kind: "scalar", min: 0.2, max: 12, default: 1.1}),
		new ParamSchema({key: "drift_sharpness", label: "Drift sharpness", kind: "scalar", min: 0.05, max: 8, default: /*0.6*/ 1}),
		// Snow is NOT pure white: the value range is narrow and high, with a faint cool cast even
		// in full light. Pure 1,1,1 clips under the specular lobe and looks like paper.
		new ParamSchema({key: "snow_ramp", label: "Snow ramp", kind: "ramp", default: colorRamp([
			{position: 0,   color_str: `rgb(209,217,230)`},   // cold snow shadow
			{position: 0.5, color_str: `rgb(229,235,245)`},   // mid snow
			{position: 1,   color_str: `rgb(247,250,255)`},   // bright highlight
		])}),
		new ParamSchema({key: "drift_height", label: "Drift relief (m)", kind: "scalar", min: 0, max: 0.5, default: 0.008}),
		new ParamSchema({key: "crust_scale", label: "Crust scale (m)", kind: "scalar", min: 0.005, max: 1, default: 0.05}),
		new ParamSchema({key: "crust_height", label: "Crust relief (m)", kind: "scalar", min: 0, max: 0.05, default: 0.001}),
		new ParamSchema({key: "glint_density", label: "Glint spacing (m)", kind: "scalar", min: 0.001, max: 0.1, default: 0.006}),
		new ParamSchema({key: "glint_ramp", label: "Glint ramp", kind: "ramp", default: [0, 0.95, 0.97, 1, 1, 1, 1, 1]}),
		new ParamSchema({key: "glint_height", label: "Glint relief (m)", kind: "scalar", min: 0, max: 0.005, default: 0.0001}),
		new ParamSchema({key: "glint_softness", label: "Glint blend softness", kind: "scalar", min: 0.00001, max: 0.01, default: 0.0002}),
		// NOTE (2026-08-09): the slope-coverage mask layer (`mask` + `maskedBy`, params
		// cover_slopeStart/cover_slopeEnd/cover_rockColor) was REMOVED. Rationale:
		//  - It duplicated a decision the splat field already makes better: slope/elevation-based
		//    material SELECTION belongs to the splat rules (elevationBand/cliffBand), which can pick
		//    a real, TEXTURED rock recipe. `maskedBy` can only ever reveal ONE FLAT `uncoveredColor`.
		//  - That flat constant (~0.33 grey) is then multiplied by hillshade, and on a steep face
		//    turned away from the light it collapsed to near-black — the recipe's most visible bug.
		//    (A separate, genuine bug also inflated the measured slope; fixed in Mask.glsl.ts by
		//    undoing the worker's slope_scale exaggeration. This layer was STILL not worth keeping.)
		//  - `contributes: "both"` also zeroed the drift/crust relief inside the masked zone, so the
		//    revealed patch was flat as well as dark.
		// If bare-rock-on-cliffs is wanted again, do it in the splat field, not here.
		new ParamSchema({key: "shadow_color", label: "Shadow tint", kind: "color", default: [0.55, 0.65, 0.85]}),
		new ParamSchema({key: "shadow_amount", label: "Shadow tint amount", kind: "scalar", min: 0, max: 1, default: 0.55}),
	],
	layers: [
		new MaterialLayer({
			id: "terrain",
			primitive: "fbm",
			args: {
				scale: {ref: "terrain_scale"},
				octaves: {ref: "terrain_octaves"},
				lacunarity: 2,
				persistence: 0.5,
				mode: 3, // turbulence — blobby mounds, not directional streaks
				heightScale: {ref: "terrain_height"},
			},
			blend: "over",
			contributes: "height",
		}),
		new MaterialLayer({
			id: "drifts",
			primitive: "bandedNoise",
			args: {
				scale: {ref: "drift_scale"},
				veining: {ref: "drift_veining"},
				sharpness: {ref: "drift_sharpness"},
				colorRamp: {ref: "snow_ramp"},
				heightScale: {ref: "drift_height"},
			},
			blend: "over",
			contributes: "both",
		}),
		new MaterialLayer({
			id: "crust",
			primitive: "fbm",
			args: {
				scale: {ref: "crust_scale"},
				octaves: 4,
				lacunarity: 2,
				persistence: 0.5,
				mode: 3, // turbulence — the crust's granular, clumped read
				heightScale: {ref: "crust_height"},
			},
			blend: "over",
			contributes: "height",
		}),
		/*new MaterialLayer({
			id: "glints",
			primitive: "scatter",
			args: {
				density: {ref: "glint_density"},
				shape: 0,
				aspect: 1,
				rotJitter: 1,
				colorRamp: {ref: "glint_ramp"},
				height: {ref: "glint_height"},
				colorJitterSeed: 13,
			},
			blend: "heightBlend",
			blendArgs: {softness: {ref: "glint_softness"}},
			contributes: "both",
		}),*/
		// The blue-shift (plan §4.4's lightModulatedRamp), LAST. Its own colour source is the snow
		// ramp at the drift mid-value, so the shift is a remap of the snow colour, not a fresh
		// pattern. (It used to be constrained to sit BEFORE the coverage mask so the mask's bare
		// rock wouldn't get tinted blue; with the mask removed that ordering constraint is moot.)
		new MaterialLayer({
			id: "shadowTint",
			primitive: "fbm",
			args: {
				scale: {ref: "drift_wavelength"},
				octaves: 1,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0,
				colorRamp: {ref: "snow_ramp"},
				heightScale: 0,
			},
			blend: "lightModulatedRamp",
			blendArgs: {shadowColor: {ref: "shadow_color"}, amount: {ref: "shadow_amount"}},
			contributes: "color",
		}),
	],
});